import { PlusIcon } from '@shopify/polaris-icons';
import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Modal,
  FormLayout,
  TextField,
  Checkbox,
  Banner,
  Tabs,
  ProgressBar,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface PackingList {
  id: number;
  name: string;
  status: 'pending' | 'in_progress' | 'completed';
  warehouse_id: number | null;
  warehouse_name: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  total_orders: number;
  total_items: number;
  packed_items: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  created_by_name: string | null;
}

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  delivery_date: string | null;
  item_count: number;
}

export default function PackingLists() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchApi, error } = useApi();
  const [packingLists, setPackingLists] = useState<PackingList[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Create packing list
  const [createModalActive, setCreateModalActive] = useState(false);
  const [availableOrders, setAvailableOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [packingListName, setPackingListName] = useState('');

  const tabs = [
    { id: 'pending', content: 'Pending' },
    { id: 'in_progress', content: 'In Progress' },
    { id: 'completed', content: 'Completed' },
    { id: 'all', content: 'All' },
  ];

  useEffect(() => {
    loadPackingLists();

    // If order param exists, open create modal with that order selected
    const orderId = searchParams.get('order');
    if (orderId) {
      loadAvailableOrders().then(() => {
        setSelectedOrders([parseInt(orderId)]);
        setCreateModalActive(true);
      });
    }
  }, [selectedTab]);

  async function loadPackingLists() {
    setLoading(true);
    const status = ['pending', 'in_progress', 'completed', 'all'][selectedTab];
    const params = status !== 'all' ? `?status=${status}` : '';
    const data = await fetchApi<{ success: boolean; packing_lists: PackingList[] }>(`/warehouse/packing-lists${params}`);
    if (data?.success && data.packing_lists) {
      setPackingLists(data.packing_lists);
    }
    setLoading(false);
  }

  async function loadAvailableOrders() {
    // Get orders that are 'picked' and ready for packing
    const data = await fetchApi<{ orders: Order[] }>('/orders?status=picked&limit=100');
    if (data?.orders) {
      setAvailableOrders(data.orders);
    }
  }

  const openCreateModal = async () => {
    await loadAvailableOrders();
    setSelectedOrders([]);
    setPackingListName(`Pack-${new Date().toISOString().split('T')[0]}`);
    setCreateModalActive(true);
  };

  const handleCreatePackingList = async () => {
    if (selectedOrders.length === 0) return;

    const result = await fetchApi<{ success: boolean; packing_list_id: number; error?: string }>('/warehouse/packing-lists', {
      method: 'POST',
      body: JSON.stringify({
        name: packingListName,
        order_ids: selectedOrders,
      }),
    });

    if (result?.success) {
      setCreateModalActive(false);
      loadPackingLists();
      // Navigate to the new packing list
      navigate(`/packing-lists/${result.packing_list_id}`);
    }
  };

  const toggleOrderSelection = (orderId: number) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleStartPacking = async (packingListId: number) => {
    await fetchApi(`/warehouse/packing-lists/${packingListId}/start`, {
      method: 'POST',
    });
    loadPackingLists();
  };

  const statusBadge = (status: PackingList['status']) => {
    const config: Record<PackingList['status'], { tone: 'success' | 'warning' | 'info'; label: string }> = {
      pending: { tone: 'warning', label: 'Pending' },
      in_progress: { tone: 'info', label: 'In Progress' },
      completed: { tone: 'success', label: 'Completed' },
    };
    const { tone, label } = config[status];
    return <Badge tone={tone}>{label}</Badge>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProgress = (pl: PackingList) => {
    if (pl.total_items === 0) return 0;
    return Math.round((pl.packed_items / pl.total_items) * 100);
  };

  return (
    <Page
      title="Packing Lists"
      primaryAction={{
        icon: PlusIcon, content: 'Create Packing List',
        onAction: openCreateModal,
      }}
      backAction={{ content: 'Warehouse', url: 'https://warehouse.whenlyapp.com' }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {packingLists.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
                  headings={['Name', 'Status', 'Orders', 'Progress', 'Assigned', 'Created', 'Actions']}
                  rows={packingLists.map((pl) => [
                    pl.name,
                    statusBadge(pl.status),
                    pl.total_orders.toString(),
                    <BlockStack gap="100">
                      <Text as="span" variant="bodySm">{pl.packed_items}/{pl.total_items} items</Text>
                      <ProgressBar progress={getProgress(pl)} tone="primary" size="small" />
                    </BlockStack>,
                    pl.assigned_to_name || '-',
                    formatDate(pl.created_at),
                    <InlineStack gap="200">
                      {pl.status === 'pending' && (
                        <Button size="slim" onClick={() => handleStartPacking(pl.id)}>
                          Start
                        </Button>
                      )}
                      {pl.status === 'in_progress' && (
                        <Button size="slim" variant="primary" onClick={() => navigate(`/packing-lists/${pl.id}`)}>
                          Continue
                        </Button>
                      )}
                      <Button size="slim" onClick={() => navigate(`/packing-lists/${pl.id}`)}>
                        View
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <BlockStack gap="200" inlineAlign="center">
                  <Text as="p" tone="subdued">
                    {loading ? 'Loading...' : 'No packing lists found.'}
                  </Text>
                  {!loading && (
                    <Button icon={PlusIcon} onClick={openCreateModal}>Create your first packing list</Button>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quick Stats */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Quick Stats</Text>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span">Pending</Text>
                  <Badge>{packingLists.filter((p) => p.status === 'pending').length.toString()}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span">In Progress</Text>
                  <Badge tone="info">{packingLists.filter((p) => p.status === 'in_progress').length.toString()}</Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span">Completed Today</Text>
                  <Badge tone="success">
                    {packingLists.filter((p) => {
                      if (p.status !== 'completed' || !p.completed_at) return false;
                      const today = new Date().toDateString();
                      return new Date(p.completed_at).toDateString() === today;
                    }).length.toString()}
                  </Badge>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create Packing List Modal */}
      <Modal
        open={createModalActive}
        onClose={() => setCreateModalActive(false)}
        title="Create Packing List"
        primaryAction={{
          icon: PlusIcon, content: 'Create',
          onAction: handleCreatePackingList,
          disabled: selectedOrders.length === 0,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setCreateModalActive(false) },
        ]}
        size="large"
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Packing List Name"
              value={packingListName}
              onChange={setPackingListName}
              autoComplete="off"
            />

            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">Select Orders</Text>
                <Text as="span" tone="subdued">
                  {selectedOrders.length} selected
                </Text>
              </InlineStack>

              {availableOrders.length > 0 ? (
                <BlockStack gap="200">
                  {availableOrders.map((order) => (
                    <div
                      key={order.id}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #e1e3e5',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: selectedOrders.includes(order.id) ? '#f3f4f6' : 'white',
                      }}
                      onClick={() => toggleOrderSelection(order.id)}
                    >
                      <InlineStack align="space-between">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="bold">
                            #{order.order_number}
                          </Text>
                          <Text as="span" tone="subdued">
                            {order.customer_name} • {order.item_count} items
                          </Text>
                        </BlockStack>
                        <BlockStack gap="100" inlineAlign="end">
                          {order.delivery_date && (
                            <Badge>
                              {new Date(order.delivery_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                              })}
                            </Badge>
                          )}
                          <div onClick={(event) => event.stopPropagation()}>
                            <Checkbox
                              label={`Select order ${order.order_number}`}
                              labelHidden
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => toggleOrderSelection(order.id)}
                            />
                          </div>
                        </BlockStack>
                      </InlineStack>
                    </div>
                  ))}
                </BlockStack>
              ) : (
                <Banner tone="info">
                  No orders available for packing. Orders must be in 'Picked' status.
                </Banner>
              )}
            </BlockStack>
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
